import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import type { RecipientItem } from '../types';

export type SelectedRecipient =
  | { kind: 'client'; clientId: string; name: string; email: string | null; phone: string | null }
  | { kind: 'contact'; contactId: string; name: string; email: string | null; phone: string | null }
  | {
      kind: 'new';
      name: string;
      email: string | null;
      phone: string | null;
    };

/**
 * Searchable recipient combobox. Pulls from /api/recipients (clients +
 * contacts; google contacts can be added by the parent setting
 * includeGoogle). When the user types and hits "+ New recipient", the
 * inline form stages a {kind:'new'} value — actual contact creation
 * happens server-side when the parent submits the send.
 *
 * `excludeClients` — when true (e.g. inside SendDocumentModal where the
 * conventional client <select> is the primary path), client-source rows
 * are stripped from results so the user isn't offered the same person twice.
 */
export function RecipientPicker({
  value,
  onChange,
  excludeClients = false,
}: {
  value: SelectedRecipient | null;
  onChange: (next: SelectedRecipient | null) => void;
  excludeClients?: boolean;
}) {
  const { t } = useTranslation(['recipients', 'common']);

  const [q, setQ] = useState('');
  const [items, setItems] = useState<RecipientItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // "+ New recipient" inline form state
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newError, setNewError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced search
  useEffect(() => {
    if (!open && !q) return;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await apiFetch<{ items: RecipientItem[] }>(
          `/api/recipients?q=${encodeURIComponent(q)}`,
        );
        setItems(excludeClients ? r.items.filter((i) => i.source !== 'client') : r.items);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [q, open, excludeClients]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowNewForm(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function pick(item: RecipientItem) {
    if (item.source === 'client') {
      onChange({
        kind: 'client',
        clientId: item.id,
        name: item.name,
        email: item.email,
        phone: item.phone,
      });
    } else if (item.source === 'contact') {
      onChange({
        kind: 'contact',
        contactId: item.id,
        name: item.name,
        email: item.email,
        phone: item.phone,
      });
    } else {
      // google — no local id yet; treat as new and rely on upsertContact
      onChange({
        kind: 'new',
        name: item.name,
        email: item.email,
        phone: item.phone,
      });
    }
    setOpen(false);
    setQ('');
  }

  function submitNew() {
    setNewError(null);
    const name = newName.trim();
    const email = newEmail.trim() || null;
    const phone = newPhone.trim() || null;
    if (!name) {
      setNewError(t('recipients:errors.name_required', { defaultValue: 'Name is required' }));
      return;
    }
    if (!email && !phone) {
      setNewError(
        t('recipients:errors.email_or_phone_required', {
          defaultValue: 'Email or phone is required',
        }),
      );
      return;
    }
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setNewError(t('recipients:errors.invalid_email', { defaultValue: 'Invalid email' }));
      return;
    }
    onChange({ kind: 'new', name, email, phone });
    setShowNewForm(false);
    setNewName('');
    setNewEmail('');
    setNewPhone('');
    setOpen(false);
    setQ('');
  }

  const display =
    value && 'name' in value
      ? `${value.name}${value.email ? ` · ${value.email}` : ''}${value.phone ? ` · ${value.phone}` : ''}`
      : '';

  return (
    <div ref={containerRef} className="recipient-picker">
      <input
        type="text"
        value={open ? q : display}
        placeholder={t('recipients:picker.placeholder', {
          defaultValue: 'Search clients & contacts…',
        })}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          if (value) onChange(null);
        }}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />

      {open && (
        <div className="recipient-picker-menu">
          {loading && (
            <div className="recipient-picker-status">
              {t('common:actions.loading', { defaultValue: 'Loading…' })}
            </div>
          )}
          {!loading && items.length === 0 && q && !showNewForm && (
            <div className="recipient-picker-status">
              {t('recipients:picker.no_results', { defaultValue: 'No matching recipients' })}
            </div>
          )}
          {!loading &&
            items.map((item) => (
              <button
                key={`${item.source}:${item.id}`}
                type="button"
                onClick={() => pick(item)}
                className="recipient-picker-row"
              >
                <div className="recipient-picker-row-top">
                  <span className="recipient-picker-name">{item.name || '—'}</span>
                  <span className="recipient-picker-tag">
                    {item.source === 'client'
                      ? t('recipients:source.client', { defaultValue: 'Client' })
                      : item.source === 'contact'
                        ? t('recipients:source.contact', { defaultValue: 'Contact' })
                        : 'Google'}
                  </span>
                </div>
                <div className="recipient-picker-sub">
                  {[item.email, item.phone].filter(Boolean).join(' · ') || '—'}
                </div>
              </button>
            ))}

          {!showNewForm && (
            <button
              type="button"
              onClick={() => {
                setShowNewForm(true);
                if (q) setNewName(q);
              }}
              className="recipient-picker-new-btn"
            >
              {t('recipients:picker.new_recipient', { defaultValue: '+ New recipient' })}
            </button>
          )}

          {showNewForm && (
            <div className="recipient-picker-new-form">
              <div className="field">
                <input
                  placeholder={t('recipients:new.name', { defaultValue: 'Name' })}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="field">
                <input
                  type="email"
                  placeholder={t('recipients:new.email', { defaultValue: 'Email (optional)' })}
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </div>
              <div className="field">
                <input
                  type="tel"
                  placeholder={t('recipients:new.phone', { defaultValue: 'Phone (optional)' })}
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                />
              </div>
              {newError && <div className="recipient-picker-error">{newError}</div>}
              <div className="recipient-picker-new-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowNewForm(false)}>
                  {t('common:actions.cancel', { defaultValue: 'Cancel' })}
                </button>
                <button type="button" className="btn-primary" onClick={submitNew}>
                  {t('recipients:new.use', { defaultValue: 'Use this' })}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
